import type { CoverageViewModel } from 'src/api/types/CoverageViewModel';

import { useParams } from 'react-router';

import Grid from '@mui/material/Grid';
import { Typography, CardContent } from '@mui/material';

import { InfoBox } from 'src/sections/employer-data/shared/components/info-box';
import { getAddressToString } from 'src/sections/employer-data/shared/utils/policyUtils';

type CoverageProfileProps = {
  coverageSummary: CoverageViewModel;
};

export default function CoverageProfile({ coverageSummary }: CoverageProfileProps) {
  const { comboId } = useParams<{ comboId: string }>();

  const showComboTooltip =
    coverageSummary?.controllingCoverageName !== coverageSummary?.mostRecentPolicy?.coverageName;

  return (
    <CardContent sx={{ flexGrow: 1 }} data-testid="coverage-profile-card">
      <Typography variant="h6" sx={{ color: 'text.primary' }} data-testid="coverage-profile-title">
        Coverage profile
      </Typography>
      <Grid container spacing={2} columns={16} sx={{ mt: 3 }}>
        <Grid size={{ xs: 16, md: 8 }}>
          <InfoBox
            label="Combo name"
            value={coverageSummary?.controllingCoverageName ?? ''}
            tooltip={
              showComboTooltip ? 'Combo name is the name of the controlling coverage.' : undefined
            }
            testId="combo-name-field"
            testIdLabel="combo-name-label"
            testIdValue="combo-name-value"
            sx={{ mt: 0 }}
          />
        </Grid>
        <Grid size={{ xs: 16, md: 8 }}>
          <InfoBox
            label="Combo ID"
            value={comboId ?? ''}
            testId="combo-id-field"
            testIdLabel="combo-id-label"
            testIdValue="combo-id-value"
            sx={{ mt: 0 }}
          />
        </Grid>
        <Grid size={{ xs: 16, md: 8 }}>
          <InfoBox
            label="Coverage name"
            value={coverageSummary?.mostRecentPolicy?.coverageName ?? ''}
            testId="coverage-name-field"
            testIdLabel="coverage-name-label"
            testIdValue="coverage-name-value"
            sx={{ mt: 0 }}
          />
        </Grid>
        <Grid size={{ xs: 16, md: 8 }}>
          <InfoBox
            label="Coverage ID"
            value={coverageSummary.coverageId ?? ''}
            testId="coverage-id-field"
            testIdLabel="coverage-id-label"
            testIdValue="coverage-id-value"
            sx={{ mt: 0 }}
          />
        </Grid>
        <Grid size={{ xs: 16 }}>
          <InfoBox
            label="Address"
            value={getAddressToString(coverageSummary?.mostRecentPolicy?.defaultAddress)}
            testId="address-field"
            testIdLabel="address-label"
            testIdValue="address-value"
            sx={{ mt: 0, whiteSpace: 'pre-line' }}
          />
        </Grid>
        <Grid size={{ xs: 16 }}>
          <InfoBox
            label="FEIN"
            value={coverageSummary.mostRecentPolicy?.feinLastFive ?? ''}
            testId="fein-field"
            testIdLabel="fein-label"
            testIdValue="fein-value"
            sx={{ mt: 0 }}
          />
        </Grid>
        <Grid size={{ xs: 16 }}>
          <InfoBox
            label="Legal nature"
            value={coverageSummary.mostRecentPolicy?.legalNatureEnumDescription ?? ''}
            testId="legal-nature-field"
            testIdLabel="legal-nature-label"
            testIdValue="legal-nature-value"
            sx={{ mt: 0 }}
          />
        </Grid>
      </Grid>
    </CardContent>
  );
}
